import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  OnboardSyncType,
  sync_onboard_crew_documents_chunk_size,
  sync_onboard_crew_documents_delete_limit_per_case
} from 'views/companies/settings/onboard-sync/config';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import NumberInput from '@/library/components/form/inputs/NumberInput';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatDisplayValue } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type OnboardSyncProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.onboard_sync;
const CATEGORY_TITLE = 'Onboard Sync';

const LABELS = {
  [sync_onboard_crew_documents_chunk_size]: 'Crew documents chunk size',
  [sync_onboard_crew_documents_delete_limit_per_case]: 'Crew documents deletion limit per case'
};

const OnboardSync: FC<OnboardSyncProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { changeField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<OnboardSyncType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [sync_onboard_crew_documents_chunk_size]:
        companySettings['sync_onboard.crew_documents.chunk_size'],
      [sync_onboard_crew_documents_delete_limit_per_case]:
        companySettings['sync_onboard.crew_documents.delete_limit_per_case']
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as OnboardSyncType | null,
    toPayload: values => [
      {
        key: sync_onboard_crew_documents_chunk_size,
        value: values[sync_onboard_crew_documents_chunk_size]
      },
      {
        key: sync_onboard_crew_documents_delete_limit_per_case,
        value: values[sync_onboard_crew_documents_delete_limit_per_case]
      }
    ],
    labels: LABELS,
    formatValue: (_key, value) => formatDisplayValue(value),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        className="cpb-2 cmb-2"
        title="Crew documents"
        hidden={
          !isSettingVisible(CATEGORY_ID, sync_onboard_crew_documents_chunk_size) &&
          !isSettingVisible(CATEGORY_ID, sync_onboard_crew_documents_delete_limit_per_case)
        }
      >
        <Row
          className="d-flex align-items-center gx-1"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col xs={3}>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={sync_onboard_crew_documents_chunk_size}
            >
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={
                  <InputLabel
                    title="CREW DOCUMENTS CHUNK SIZE"
                    apiKey={sync_onboard_crew_documents_chunk_size}
                  />
                }
                placeholder="Add no."
                decimalScale={0}
                onChange={changeField(sync_onboard_crew_documents_chunk_size)}
                {...fields[sync_onboard_crew_documents_chunk_size]}
              />
            </VisibleSetting>
          </Col>
          <Col>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={sync_onboard_crew_documents_delete_limit_per_case}
            >
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={
                  <InputLabel
                    title="CREW DOCUMENTS DELETION LIMIT PER CASE"
                    apiKey={sync_onboard_crew_documents_delete_limit_per_case}
                  />
                }
                placeholder="Add no."
                decimalScale={0}
                onChange={changeField(sync_onboard_crew_documents_delete_limit_per_case)}
                {...fields[sync_onboard_crew_documents_delete_limit_per_case]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>
    </Category>
  );
};

export default OnboardSync;
